/**
 * Beni Video List
 * - 区域：店面按访客国家由服务端直接匹配并渲染，无选择器；
 *   主题编辑器内（request.design_mode）渲染所有区域并显示下拉，供运营预览各国内容
 * - 单市场单国兜底：市场内只启用 1 个国家时服务端 country 对所有人相同，改由客户端按真实 IP
 *   判定区域（data-client-region / data-region-codes，取数同 beni-region-switch-bar）。
 *   检测失败或未命中任何区域时保持服务端渲染的默认区域，不闪屏。
 * - View more：点击展开当前区域超出初始数量的卡片
 * - 弹层播放：监听 yg-video 冒泡的 `YG:openPopup`，把其 <template> 克隆进 Dawn 的
 *   modal-dialog（宿主自动移到 <body>，自带锁滚动/焦点陷阱/Esc/点遮罩关闭）播放
 */
class BeniVideoList extends HTMLElement {
  constructor() {
    super();
    this.expanded = new Set();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onOpenPopup = this.onOpenPopup.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
  }

  get effectiveInitial() {
    const isMobile = this.mobileQuery ? this.mobileQuery.matches : false;
    return isMobile ? this.initialMobile : this.initialDesktop;
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-beni-vl-card]'));
    this.moreBtn = this.querySelector('[data-beni-vl-more]');
    this.moreLabelEl = this.querySelector('[data-beni-vl-more-label]');
    this.regionRoot = this.querySelector('[data-beni-vl-region]');
    this.regionToggle = this.querySelector('[data-beni-vl-region-toggle]');
    this.regionMenu = this.querySelector('[data-beni-vl-region-menu]');
    this.regionCurrent = this.querySelector('[data-beni-vl-region-current]');
    this.regionOptions = Array.from(this.querySelectorAll('[data-beni-vl-region-option]'));

    // 弹窗宿主是 Dawn 的 modal-dialog，connect 时会把自己移到 <body>，故按 id 从 document 取，
    // 而不是 this.querySelector（移走后就不再是本元素的子节点）
    this.sectionId = this.dataset.sectionId || '';
    this.modal = document.getElementById('BeniVideoModal-' + this.sectionId);
    this.modalBody = this.modal ? this.modal.querySelector('[data-beni-vl-lightbox-body]') : null;

    this.initialDesktop = parseInt(this.dataset.initial, 10) || 0;
    this.initialMobile = parseInt(this.dataset.initialMobile, 10) || this.initialDesktop;
    this.activeRegion = this.dataset.activeRegion || '';
    this.clientRegion = this.dataset.clientRegion === 'true';
    this.regionCodes = this.parseRegionCodes(this.dataset.regionCodes);

    // 断点切换时按当前断点的初始数量重新计算
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener('change', this.onBreakpointChange);
    } else if (this.mobileQuery.addListener) {
      this.mobileQuery.addListener(this.onBreakpointChange);
    }

    this.bindRegion();
    this.bindMore();
    this.bindMedia();

    // yg-video 在 popup 模式点击时会冒泡 `YG:openPopup`；在本 section 内接住，避免影响其它 section
    this.addEventListener('YG:openPopup', this.onOpenPopup);

    // 关闭走 modal-dialog 自带的 关闭按钮 / 点遮罩 / Esc；用 open 属性变化统一收尾（见 onModalToggle）
    if (this.modal) {
      this.modalObserver = new MutationObserver(() => this.onModalToggle());
      this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    }

    this.render();

    // 单市场单国兜底：服务端 country 不可用，改按真实 IP 判定区域
    if (this.clientRegion && !this.isDesignMode) {
      // 测试用：?region_test=CN 强制以该国家码切换（跳过 IP 检测），便于在预览链接直接验证
      const testCode = new URLSearchParams(window.location.search).get('region_test');
      if (testCode) {
        this.applyClientRegion(this.findRegionByCountry(testCode.trim().toUpperCase()));
        return;
      }
      this.detectClientRegion();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('YG:openPopup', this.onOpenPopup);
    this.modalObserver?.disconnect();
    // modal-dialog 已把自身移到 <body>；section 卸载/编辑器重渲染时先收尾再移除，避免残留累积
    this.modal?.hide?.();
    this.modal?.remove();
    if (this.mobileQuery?.removeEventListener) {
      this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
    } else if (this.mobileQuery?.removeListener) {
      this.mobileQuery.removeListener(this.onBreakpointChange);
    }
  }

  onBreakpointChange() {
    this.render();
  }

  /* ---------- 单市场单国兜底：按访客真实 IP 判定区域 ---------- */

  // data-region-codes 形如 {"block-id":"CN,HK,MO,TW"}，解析为 [{id, codes:[...]}]
  parseRegionCodes(raw) {
    if (!raw) return [];
    let map;
    try {
      map = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    if (!map || typeof map !== 'object') return [];

    return Object.keys(map).map((id) => ({
      id: id,
      codes: String(map[id] || '')
        .split(',')
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean),
    }));
  }

  findRegionByCountry(country) {
    if (!country) return null;
    const hit = this.regionCodes.find((region) => region.codes.indexOf(country) !== -1);
    return hit ? hit.id : null;
  }

  detectClientRegion() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root.replace(/\/$/, '') + '/browsing_context_suggestions.json?country[enabled]=true';

    // 同页可能还有 beni-footer-icp / beni-region-switch-bar 在检测，共用一次请求
    if (!window.__beniBcsPromise) {
      window.__beniBcsPromise = fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; });
    }

    window.__beniBcsPromise.then(this.onClientRegionDetected.bind(this));
  }

  onClientRegionDetected(data) {
    if (!data || !this.isConnected) return;

    var suggestion = data.suggestions && data.suggestions[0];
    var part = suggestion && suggestion.parts && suggestion.parts.country;
    // 用 detected_values（按真实 IP），suggestions 受「已启用市场」限制，单市场下基本为空
    var detected = (
      (data.detected_values && data.detected_values.country && data.detected_values.country.handle) ||
      (part && part.handle) ||
      ''
    ).toUpperCase();

    var regionId = this.findRegionByCountry(detected);
    // 未配置该国家 / 检测失败 → 保持服务端渲染的默认区域
    if (!regionId) return;

    this.applyClientRegion(regionId);
  }

  // 直接设置激活区域并重算显示（含 View more 计数）；不进入 expanded
  applyClientRegion(regionId) {
    if (!regionId || regionId === this.activeRegion) return;

    this.activeRegion = regionId;
    this.dataset.activeRegion = regionId;
    this.render();
  }

  get isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* ---------- 显示计算 ---------- */

  render() {
    const active = this.activeRegion;
    const expanded = this.expanded.has(active);
    const initial = this.effectiveInitial;
    let activeTotal = 0;

    this.cards.forEach((card) => {
      // 只显示当前激活区域的卡片（无 data-region 时视为激活；客户端兜底模式下所有区域都在 DOM 中）
      const region = card.dataset.region;
      const inActive = region === undefined || region === active;
      if (inActive) activeTotal += 1;

      const idx = parseInt(card.dataset.index, 10) || 0;
      const overflow = initial > 0 && idx >= initial;
      const visible = inActive && (!overflow || expanded);

      card.hidden = !visible;
    });

    if (this.moreBtn) {
      const hasOverflow = initial > 0 && activeTotal > initial;
      this.moreBtn.hidden = !hasOverflow;

      if (hasOverflow) {
        this.moreBtn.classList.toggle('is-expanded', expanded);
        this.moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (this.moreLabelEl) {
          this.moreLabelEl.textContent = expanded
            ? this.moreBtn.dataset.lessLabel || 'Show less'
            : this.moreBtn.dataset.moreLabel || 'View more';
        }
      }
    }
  }

  /* ---------- 区域切换（仅编辑器内存在下拉时生效） ---------- */

  bindRegion() {
    if (!this.regionRoot || !this.regionToggle) return;

    this.regionToggle.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleMenu();
    });

    this.regionOptions.forEach((option) => {
      option.addEventListener('click', () => {
        this.switchRegion(option.dataset.beniVlRegionOption, option);
        this.closeMenu();
      });
    });
  }

  toggleMenu() {
    if (this.regionMenu?.hidden) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  openMenu() {
    if (!this.regionMenu) return;
    this.regionMenu.hidden = false;
    this.regionToggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  closeMenu() {
    if (!this.regionMenu) return;
    this.regionMenu.hidden = true;
    this.regionToggle?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onDocumentClick(event) {
    if (this.regionRoot && !this.regionRoot.contains(event.target)) {
      this.closeMenu();
    }
  }

  switchRegion(id, option) {
    if (!id || id === this.activeRegion) return;

    this.activeRegion = id;
    this.dataset.activeRegion = id;
    this.expanded.clear();

    if (this.regionCurrent && option) {
      this.regionCurrent.textContent = option.dataset.beniVlRegionLabel || option.textContent.trim();
    }

    this.regionOptions.forEach((opt) => {
      opt.classList.toggle('is-active', opt === option);
      opt.setAttribute('aria-selected', opt === option ? 'true' : 'false');
    });

    this.render();
  }

  /* ---------- View more ---------- */

  bindMore() {
    this.moreBtn?.addEventListener('click', () => {
      if (this.expanded.has(this.activeRegion)) {
        this.expanded.delete(this.activeRegion);
      } else {
        this.expanded.add(this.activeRegion);
      }
      this.render();
    });
  }

  /* ---------- 缩略图点击 → 触发 yg-video 弹层 ---------- */

  bindMedia() {
    this.cards.forEach((card) => {
      const media = card.querySelector('[data-beni-vl-media]');
      if (!media) return;
      media.addEventListener('click', () => this.playCard(media));
    });
  }

  playCard(media) {
    const video = media.querySelector('yg-video');
    if (!video) return;

    // 记录触发元素，供 modal-dialog 关闭时把焦点还给它
    this.lastTrigger = media;

    if (typeof video.playVideo === 'function') {
      video.playVideo();
    } else if (window.customElements) {
      window.customElements.whenDefined('yg-video').then(() => video.playVideo?.());
    }
  }

  /* ---------- 弹层播放（复用 Dawn 的 modal-dialog） ---------- */

  onOpenPopup(event) {
    const template = event.detail?.popupTemplate;
    if (!template || !this.modal || !this.modalBody) return;

    this.populateModal(template);

    // modal-dialog.show 负责锁滚动 / trapFocus / pauseAllMedia；随后仍在用户手势栈内 play，
    // 规避带声音的 autoplay 被浏览器拦截
    if (typeof this.modal.show === 'function') {
      this.modal.show(this.lastTrigger);
    } else {
      this.modal.setAttribute('open', '');
    }
    this.playModalVideos();
  }

  populateModal(template) {
    const fragment = template.content.cloneNode(true);
    // 只保留当前断点的视频/iframe，剔除另一断点，避免同时加载两路源
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const offClass = isMobile ? 'hide-mobile' : 'hide-pc';
    fragment.querySelectorAll('.' + offClass).forEach((el) => el.remove());
    this.modalBody.replaceChildren(fragment);
  }

  playModalVideos() {
    this.modalBody.querySelectorAll('video').forEach((video) => {
      const request = video.play();
      if (request && request.catch) request.catch(() => {});
    });
  }

  // open 属性被移除时收尾（关闭按钮 / 点遮罩 / Esc 任一路径都会触发）：
  // 暂停并清空 body，一并销毁 iframe 以停止 YouTube/Vimeo 播放
  onModalToggle() {
    if (!this.modal || this.modal.hasAttribute('open')) return;
    this.modalBody?.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch (e) {
        /* noop */
      }
    });
    this.modalBody?.replaceChildren();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    // 弹窗的 Esc 由 modal-dialog 自行处理；这里只负责地区下拉菜单
    this.closeMenu();
  }
}

if (!window.customElements.get('beni-video-list')) {
  window.customElements.define('beni-video-list', BeniVideoList);
}
